import {
  optimizeImageUrlFillRectangle,
  optimizeImageUrlFillSquare,
} from "@faire/web--source/common/images/optimizeImages";
import { useIsSSR } from "@faire/web--source/common/server-side-rendering/isSSR";
import { isNotUndefined } from "@faire/web--source/common/typescriptUtils";
import { Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { ILayoutElementData } from "@faire/web-api--source/indigofair/data/ILayoutElementData";
import { ISearchQueryTile } from "@faire/web-api--source/indigofair/data/ISearchQueryTile";
import React from "react";

import { FiniteCarouselBase } from "@faire/retailer/components/LayoutElementRenderer/__internal__/Carousel/ByType/FiniteCarouselBase";
import { Context } from "@faire/retailer/components/LayoutElementRenderer/__internal__/Context";
import { PillStyles } from "@faire/retailer/components/LayoutElementRenderer/styles/PillStyles";

import { BaseCarouselProps } from "../CarouselBase";

export const SearchPills: React.FC<BaseCarouselProps> = () => {
  const { layoutElement, onItemClick, requestId } = React.useContext(Context);
  const isSSR = useIsSSR();

  const renderItem = (item: ISearchQueryTile, index: number) => {
    if (!item.query || !item.image || !item.cta_link) {
      return null;
    }

    const isCountryFlag =
      item.image.tags.length > 0 &&
      item.image.tags.includes("country_flag_image");

    const imageUrl = isCountryFlag
      ? optimizeImageUrlFillRectangle(
          item.image,
          isSSR,
          PillStyles.COUNTRY_FLAG_WIDTH,
          PillStyles.COUNTRY_FLAG_HEIGHT,
          {
            ignoreResizeTag: true,
          }
        )
      : optimizeImageUrlFillSquare(
          item.image,
          isSSR,
          PillStyles.SQUARE_DIMENSION,
          {
            ignoreResizeTag: true,
          }
        );

    const onClick = () => {
      if (layoutElement.on_click_event_name && item.query) {
        onItemClick?.(
          layoutElement.on_click_event_name ?? "",
          requestId,
          item.query
        );
      }
    };

    return (
      <React.Fragment key={`${item.query}_${index}`}>
        <PillStyles.Pill
          linkTo={item.cta_link}
          onClick={onClick}
          $isCountryFlag={isCountryFlag}
        >
          <img src={imageUrl} alt={item.query} loading="lazy" />
          <Spacer width="1x" />
          <Typography>{item.query}</Typography>
        </PillStyles.Pill>
      </React.Fragment>
    );
  };

  const getItems = () => {
    return layoutElement.layout_element_data
      .map((elementData: ILayoutElementData) => elementData.search_query_tile)
      .filter(isNotUndefined);
  };

  const items = getItems();
  return (
    <FiniteCarouselBase
      renderItem={renderItem}
      items={items}
      rows={items.length / 4}
      itemGap={16}
      shouldHideRightButton
    />
  );
};
